import React, { useContext } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { Badge, Button, Card, CardBody, CardHeader, Col, Row } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAngleRight,
  faCashRegister,
  faCheckCircle,
  faClock,
  faExclamationTriangle,
  faFileInvoiceDollar,
  faHandHoldingUsd,
  faLandmark,
  faMoneyBillWave,
  faPiggyBank,
  faUniversity,
} from "@fortawesome/free-solid-svg-icons";
import Loader from "../../common/Loader";
import Background from "../../common/Background";
import corner1 from "../../../assets/img/illustrations/corner-1.png";
import corner2 from "../../../assets/img/illustrations/corner-2.png";
import corner3 from "../../../assets/img/illustrations/corner-3.png";
import { CurrencyContext } from "../../../context/Context";
import Money, { ConvertedMoney } from "./Money";
import CondominiumMovements from "./CondominiumMovements";
import { sectionHref } from "./sections";

const sumBalances = (accounts) =>
  accounts.reduce((sum, account) => sum + parseFloat(account.balance || 0), 0);

const plural = (count, one, many) => `${count} ${count === 1 ? one : many}`;

// Saldo disponible de cajas y bancos, destacado como lo estaba en la página
// de Finanzas anterior.
const AvailableHero = ({ value, loading }) => {
  const { currency: curr } = useContext(CurrencyContext);
  const rate = Number(curr?.value || 1);

  return (
    <Card className="border-0 shadow-sm overflow-hidden bg-primary mb-3" style={{ borderRadius: 15 }}>
      <CardBody className="p-4 text-white position-relative">
        <div
          className="position-absolute d-none d-sm-block"
          style={{ right: "5%", top: "50%", transform: "translateY(-50%)", opacity: 0.15 }}
        >
          <FontAwesomeIcon icon={faMoneyBillWave} style={{ fontSize: "8rem" }} />
        </div>
        <h6 className="text-uppercase text-white fs--2 mb-1 font-weight-bold">
          Disponible hoy (cajas + bancos)
        </h6>
        <h1 className="display-4 text-white mb-2 font-weight-bold">
          {loading ? "…" : <Money value={value} />}
        </h1>
        {!loading && curr?.currency?.iso && rate !== 1 && (
          <Badge color="soft-light" pill className="px-3 py-1 fs--2">
            <FontAwesomeIcon icon={faLandmark} className="mr-2" />
            <ConvertedMoney value={value} />
          </Badge>
        )}
      </CardBody>
    </Card>
  );
};

AvailableHero.propTypes = {
  value: PropTypes.number.isRequired,
  loading: PropTypes.bool,
};

// Cifra de cabecera con el fondo ilustrado de las tarjetas de Finanzas. Con
// `to` toda la tarjeta lleva a su detalle.
const StatCard = ({ icon, color, corner, label, caption, value, valueClass, to, linkLabel }) => (
  <Card
    className={`border shadow-none h-100 overflow-hidden ${to ? "hover-shadow" : ""}`}
    style={{ borderRadius: 12 }}
  >
    <Background image={corner} className="bg-card" />
    <CardBody className="p-3 d-flex flex-column position-relative">
      <div className="d-flex align-items-center mb-3">
        <div className={`bg-soft-${color} p-2 rounded-circle mr-3`}>
          <FontAwesomeIcon icon={icon} fixedWidth className={`text-${color} fs-0`} />
        </div>
        <div>
          <h6 className="mb-0 text-700 font-weight-bold fs--1">{label}</h6>
          <span className="fs--2 text-500">{caption}</span>
        </div>
      </div>
      <h4 className={`font-weight-bold mb-1 ${valueClass || ""}`}>{value}</h4>
      {to && (
        <div className="mt-auto pt-2 border-top">
          <Link to={to} className={`stretched-link fs--2 font-weight-semi-bold text-${color}`}>
            {linkLabel}
            <FontAwesomeIcon icon={faAngleRight} className="ml-1" />
          </Link>
        </div>
      )}
    </CardBody>
  </Card>
);

StatCard.propTypes = {
  icon: PropTypes.object.isRequired,
  color: PropTypes.string.isRequired,
  corner: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  caption: PropTypes.node,
  value: PropTypes.node.isRequired,
  valueClass: PropTypes.string,
  to: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  linkLabel: PropTypes.string,
};

const AttentionItem = ({ icon, color, title, detail, to, actionLabel, primary }) => (
  <div className="d-flex align-items-center p-3 mb-2 rounded-soft bg-light">
    <div
      className={`bg-soft-${color} rounded-circle d-flex flex-center mr-3 flex-shrink-0`}
      style={{ width: 36, height: 36 }}
    >
      <FontAwesomeIcon icon={icon} className={`text-${color}`} />
    </div>
    <div className="min-w-0 mr-2" style={{ flex: 1 }}>
      <h6 className="mb-0">{title}</h6>
      {detail && <p className="fs--1 text-600 mb-0">{detail}</p>}
    </div>
    <Button
      tag={Link}
      to={to}
      size="sm"
      color={primary ? "primary" : "falcon-default"}
      className="flex-shrink-0"
    >
      {actionLabel}
    </Button>
  </div>
);

AttentionItem.propTypes = {
  icon: PropTypes.object.isRequired,
  color: PropTypes.string.isRequired,
  title: PropTypes.node.isRequired,
  detail: PropTypes.node,
  to: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
  actionLabel: PropTypes.string.isRequired,
  primary: PropTypes.bool,
};

// Lo que el manager tiene pendiente de hacer. Solo avisa de lo que se puede
// saber con los datos que ya trae la pantalla.
const Attention = ({ data, base }) => {
  const pendingPays = data.pendingPays.total;
  const pendingDebts = data.pendingDebts.length;

  return (
    <Card className="h-100">
      <CardHeader>
        <h5 className="mb-0">Requiere tu atención</h5>
      </CardHeader>
      <CardBody className="pt-0">
        {data.loading || data.pendingPays.loading ? (
          <Loader />
        ) : pendingPays === 0 && pendingDebts === 0 ? (
          <div className="d-flex align-items-center p-3 rounded-soft bg-light">
            <FontAwesomeIcon icon={faCheckCircle} className="text-success mr-3 fs-2" />
            <div>
              <h6 className="mb-0">Todo al día</h6>
              <p className="fs--1 text-600 mb-0">
                No hay pagos por aprobar ni cuentas por pagar pendientes.
              </p>
            </div>
          </div>
        ) : (
          <>
            {pendingPays > 0 && (
              <AttentionItem
                icon={faClock}
                color="primary"
                title={`${plural(
                  pendingPays,
                  "pago de propietario espera",
                  "pagos de propietarios esperan"
                )} aprobación`}
                detail="Apruébalos o recházalos para que el saldo de cada propiedad quede al día."
                to={sectionHref("collections")}
                actionLabel="Revisar"
                primary
              />
            )}
            {pendingDebts > 0 && (
              <AttentionItem
                icon={faFileInvoiceDollar}
                color="warning"
                title={`${plural(pendingDebts, "cuenta por pagar pendiente", "cuentas por pagar pendientes")}`}
                detail={
                  <>
                    Suman <Money value={data.totalPendingDebts} /> entre todos los proveedores.
                  </>
                }
                to={`${base}/topay`}
                actionLabel="Ver cuentas"
              />
            )}
          </>
        )}
      </CardBody>
    </Card>
  );
};

Attention.propTypes = {
  data: PropTypes.object.isRequired,
  base: PropTypes.string.isRequired,
};

const bankCurrencyLabel = (account) =>
  account?.currency?.iso ? `${account.currency.iso} · ${account.currency.name}` : null;

const AccountRow = ({ account, base }) => {
  const isBank = account.type === "BANK";
  const title = isBank
    ? `${account.bank_name || "Banco"}${
        account.account_number ? ` ···· ${String(account.account_number).slice(-4)}` : ""
      }`
    : "Caja efectivo";

  return (
    <Link
      to={`${base}/account-movements/${account.id}`}
      className="d-flex align-items-center py-2 px-1 border-bottom border-200 text-900 hover-text-decoration-none"
    >
      <div
        className={`bg-soft-${isBank ? "info" : "success"} rounded-soft d-flex flex-center mr-3 flex-shrink-0`}
        style={{ width: 34, height: 34 }}
      >
        <FontAwesomeIcon
          icon={isBank ? faUniversity : faCashRegister}
          className={`text-${isBank ? "info" : "success"}`}
        />
      </div>
      <div className="min-w-0 mr-2" style={{ flex: 1 }}>
        <h6 className="mb-0 text-truncate">{title}</h6>
        {bankCurrencyLabel(account) && (
          <span className="fs--2 text-600">{bankCurrencyLabel(account)}</span>
        )}
      </div>
      <div className="text-right text-nowrap">
        <Money value={account.balance} className="d-block font-weight-semi-bold" />
        <ConvertedMoney
          converted={
            account?.currency
              ? Number(account.balance_bs || 0)
              : undefined
          }
          value={account.balance}
          className="d-block fs--2 text-600"
        />
      </div>
      <FontAwesomeIcon icon={faAngleRight} className="text-400 ml-2" />
    </Link>
  );
};

AccountRow.propTypes = {
  account: PropTypes.object.isRequired,
  base: PropTypes.string.isRequired,
};

// Cajas y bancos siempre a la vista: antes los bancos quedaban detrás de un
// "Ver cuentas" que desplegaba otra lista de tarjetas.
const Accounts = ({ accounts, loading, base }) => {
  const listed = accounts.filter((account) => ["CURRENT", "BANK"].includes(account.type));

  return (
    <Card className="h-100">
      <CardHeader className="d-flex justify-content-between align-items-center">
        <h5 className="mb-0">Cuentas</h5>
        <Link to="/dashboard/banks" className="fs--1 font-weight-semi-bold">
          Administrar bancos
        </Link>
      </CardHeader>
      <CardBody className="pt-0">
        {loading ? (
          <Loader />
        ) : listed.length > 0 ? (
          listed.map((account) => <AccountRow key={account.id} account={account} base={base} />)
        ) : (
          <p className="fs--1 text-600 mb-0">
            Todavía no hay cajas ni cuentas bancarias registradas.
          </p>
        )}
      </CardBody>
    </Card>
  );
};

Accounts.propTypes = {
  accounts: PropTypes.array.isRequired,
  loading: PropTypes.bool,
  base: PropTypes.string.isRequired,
};

const FinanceSummary = ({ data, base, role, condominiumId }) => {
  const isManager = role === "manager";
  const { accounts } = data;
  const available = sumBalances(accounts.filter((account) => account.type !== "ADVANCES"));
  const reserve = sumBalances(accounts.filter((account) => account.type === "ADVANCES"));
  const statCol = isManager ? { md: 4 } : { md: 6 };
  const loadingValue = <span className="text-400">…</span>;
  const hasPendingDebts = data.pendingDebts.length > 0;

  return (
    <>
      <AvailableHero value={available} loading={data.loading} />

      <Row>
        {isManager && (
          <Col {...statCol} className="mb-3">
            <StatCard
              icon={faHandHoldingUsd}
              color="success"
              corner={corner3}
              label="Por cobrar"
              caption="Lo que deben los propietarios"
              value={
                data.receivableTotal.loading ? (
                  loadingValue
                ) : (
                  <Money value={data.receivableTotal.value} />
                )
              }
              to={sectionHref("collections")}
              linkLabel="Ver cobros"
            />
          </Col>
        )}
        <Col {...statCol} className="mb-3">
          <StatCard
            icon={faExclamationTriangle}
            color={hasPendingDebts ? "danger" : "success"}
            corner={corner1}
            label="Por pagar"
            caption={
              hasPendingDebts
                ? plural(data.pendingDebts.length, "cuenta pendiente", "cuentas pendientes")
                : "¡Al día!"
            }
            value={data.loading ? loadingValue : <Money value={data.totalPendingDebts} />}
            valueClass={hasPendingDebts ? "text-danger" : "text-dark"}
            to={isManager ? sectionHref("expenses") : `${base}/topay`}
            linkLabel={isManager ? "Ver gastos" : "Ver cuentas"}
          />
        </Col>
        <Col {...statCol} className="mb-3">
          <StatCard
            icon={faPiggyBank}
            color="warning"
            corner={corner2}
            label="Fondo de reserva"
            caption="Ahorros"
            value={data.loading ? loadingValue : <Money value={reserve} />}
            valueClass="text-warning"
            to={`${base}/reserve-funds`}
            linkLabel="Ver fondos"
          />
        </Col>
      </Row>

      <Row>
        {isManager && (
          <Col lg={7} className="mb-3">
            <Attention data={data} base={base} />
          </Col>
        )}
        <Col lg={isManager ? 5 : 12} className="mb-3">
          <Accounts accounts={accounts} loading={data.loading} base={base} />
        </Col>
      </Row>

      <CondominiumMovements condominiumId={condominiumId} role={role} />
    </>
  );
};

FinanceSummary.propTypes = {
  data: PropTypes.object.isRequired,
  base: PropTypes.string.isRequired,
  role: PropTypes.string,
  condominiumId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default FinanceSummary;
